import { AppFrame, RouterProvider, Routes, SessionProvider, Spinner, UpdateBanner, useRouter, type RouteTable } from '@khaddi/ui';
import { useEffect, type ReactNode } from 'react';
import { OrderReadyAlert } from './components/OrderReadyAlert';
import { RequireAuth } from './components/RequireAuth';
import { SESSION_STORAGE_KEY } from './config';
import { AccountScreen } from './screens/AccountScreen';
import { CheckoutScreen } from './screens/CheckoutScreen';
import { HomeScreen } from './screens/HomeScreen';
import { InstallScreen } from './screens/InstallScreen';
import { LoginScreen } from './screens/LoginScreen';
import { LoyaltyScreen } from './screens/LoyaltyScreen';
import { MenuScreen } from './screens/MenuScreen';
import { OrdersScreen } from './screens/OrdersScreen';
import { ProductScreen } from './screens/ProductScreen';
import { ShipperSignupScreen } from './screens/ShipperSignupScreen';
import { TrackingScreen } from './screens/TrackingScreen';
import { VerifyScreen } from './screens/VerifyScreen';
import { AppConfigProvider, useAppConfig } from './state/appConfig';
import { CartProvider } from './state/cart';
import { CatalogProvider } from './state/catalog';

const routes: RouteTable = [
  { pattern: '/', render: () => <HomeScreen /> },
  { pattern: '/menu', render: () => <MenuScreen /> },
  { pattern: '/product/:id', render: ({ id }) => <ProductScreen key={id} productId={id ?? ''} /> },
  { pattern: '/login', render: () => <LoginScreen /> },
  { pattern: '/install', render: () => <InstallScreen /> },
  { pattern: '/checkout', render: () => <OnlyWhen feature="onlineOrdering"><RequireAuth><CheckoutScreen /></RequireAuth></OnlyWhen> },
  { pattern: '/orders', render: () => <RequireAuth><OrdersScreen /></RequireAuth> },
  { pattern: '/orders/:id', render: ({ id }) => <RequireAuth><TrackingScreen key={id} orderId={id ?? ''} /></RequireAuth> },
  { pattern: '/rewards', render: () => <RequireAuth><LoyaltyScreen /></RequireAuth> },
  { pattern: '/account', render: () => <RequireAuth><AccountScreen /></RequireAuth> },
  { pattern: '/shipper-signup', render: () => <OnlyWhen feature="delivery"><RequireAuth><ShipperSignupScreen /></RequireAuth></OnlyWhen> },
  { pattern: '/verify', render: () => <RequireAuth><VerifyScreen /></RequireAuth> },
];

/** Unknown paths go home, replacing the bad entry so Back doesn't return to it. */
const RedirectHome = () => {
  const { navigate } = useRouter();
  useEffect(() => navigate('/', { replace: true }), [navigate]);
  return <Spinner />;
};

/** Screens the store has switched off in the console (old links, bookmarks) fall back to home. */
const OnlyWhen = ({ feature, children }: { feature: 'onlineOrdering' | 'delivery'; children: ReactNode }) => {
  const { features, loaded } = useAppConfig();
  if (!loaded) return <Spinner />;
  return features[feature] ? <>{children}</> : <RedirectHome />;
};

export const App = () => (
  <SessionProvider storageKey={SESSION_STORAGE_KEY}>
    <RouterProvider>
      <AppConfigProvider>
        <CartProvider>
          <CatalogProvider>
            <AppFrame defaultNav="top">
              <UpdateBanner />
              <Routes table={routes} fallback={<RedirectHome />} />
              <OrderReadyAlert />
            </AppFrame>
          </CatalogProvider>
        </CartProvider>
      </AppConfigProvider>
    </RouterProvider>
  </SessionProvider>
);
